/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Common client to handle graph streaming and synchronous requests.
 *
 * @file GraphClient.js
 * @author Satyaki Bose
 */

import { fetchEventSource } from '@microsoft/fetch-event-source';
import Framework from '@greenville/framework';
import { getSnapshot } from 'mobx-state-tree';
import env from '../../../common/env';

const STREAM_EVENTS = {
  FATAL: 'FatalError',
  STATUS: {
    START: 'start',
    KEEP_ALIVE: 'keepAlive',
    STREAMING: 'streaming',
    DONE: 'done'
  },
  TYPE: { CHUNK: 'chunk' }
};

const IGNORED_STREAM_STATUSES = new Set(Object.values(STREAM_EVENTS.STATUS));

const MESSAGES = {
  PARSE_FAILED: 'Failed to parse graph stream event',
  FATAL: 'Graph stream fatal error',
  ERROR: 'Graph stream error',
  CLOSED_EARLY: 'Graph stream closed before completion',
  CONFIG_MISSING: 'graphId/version not configured',
  unexpectedEvent: (status) => `Unexpected graph stream event: ${status || 'unknown'}`
};

/** Stops fetch-event-source from auto-retrying the POST. */
class GraphStreamFatalError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'GraphStreamFatalError';
  }
}

export function buildGraphHeaders(useStream = false) {
  return {
    Accept: useStream ? 'text/event-stream' : 'application/json',
    'Content-Type': 'application/json',
    ...(useStream ? { usestream: true } : {}),
    'x-berlin-tenant-id': env.BERLIN.TENANT_ID,
    'x-berlin-tenant-key': env.BERLIN.TENANT_KEY
  };
}

function parseStreamEventData(data) {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    throw new Error(MESSAGES.PARSE_FAILED);
  }
}

export function runGraphSync(client, { graphId, graphVersion, payload, headers }) {
  return client.post(
    `/1.0/run-graph/${graphId}/versions/${graphVersion}`,
    payload,
    { headers }
  );
}

export function runGraphStream({ graphId, graphVersion, payload, headers, signal }) {
  const user = getSnapshot(Framework.getStoreRegistry().getStore('user'));
  const token = user?.token;
  const url = `${env.BERLIN.STREAM_BASE_URL}/1.0/run-graph/${graphId}/versions/${graphVersion}`;

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };

    const stopStream = (error) => {
      finish(reject, error);
      throw new GraphStreamFatalError(error?.message || MESSAGES.ERROR, { cause: error });
    };

    fetchEventSource(url, {
      method: 'POST',
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload),
      signal,
      openWhenHidden: true,
      onmessage(msg) {
        if (msg.event === STREAM_EVENTS.FATAL) {
          stopStream(new Error(MESSAGES.FATAL));
        }

        try {
          if (!msg.data) {
            return;
          }
          const parsed = parseStreamEventData(msg.data);
          if (!parsed) {
            return;
          }
          // now content is single chunk
          const { streamStatus, type, content } = parsed;
          if (type === STREAM_EVENTS.TYPE.CHUNK) {
            finish(resolve, { data: { output: content || '' } });

            return;
          }
          if (!IGNORED_STREAM_STATUSES.has(streamStatus)) {
            stopStream(new Error(MESSAGES.unexpectedEvent(streamStatus)));
          }
        } catch (error) {
          stopStream(error);
        }
      },
      onerror(err) {
        if (!settled) {
          finish(reject, err || new Error(MESSAGES.ERROR));
        }
        throw new GraphStreamFatalError(err?.message || MESSAGES.ERROR, { cause: err });
      },
      onclose() {
        if (!settled) {
          stopStream(new Error(MESSAGES.CLOSED_EARLY));
        }
      }
    }).catch((err) => {
      if (!settled) {
        finish(reject, err);
      }
    });
  });
}

export function runGraph({ client, graphConfig, payload, signal }) {
  const { graphId, graphVersion, useStream } = graphConfig || {};
  const headers = buildGraphHeaders(useStream);
  const args = { graphId, graphVersion, payload, headers };

  return useStream
    ? runGraphStream({ ...args, signal })
    : runGraphSync(client, args);
}

/** @param {{ graphId?: string, version?: number, graphVersion?: number, useStream?: boolean }} config */
export function resolveGraphConfig(config = {}) {
  const { graphId, version, graphVersion, useStream = false } = config;
  const resolvedVersion = graphVersion ?? version;

  if (!graphId || resolvedVersion == null) {
    throw new Error(MESSAGES.CONFIG_MISSING);
  }

  return { graphId, graphVersion: resolvedVersion, useStream };
}
